import React, {
  useCallback, useMemo, useState,
} from 'react';
import {
  Box,
  Inscription,
  Preloader,
  RoundPendingIcon,
  ShimmerBox,
} from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { WarnModal } from '@/components/elements';

import { useTerminatePlan } from '../hooks';
import {
  useCancelPlanTerminationSuccessModalStore,
} from '../store';

import { useCancelPlanTermination } from './hooks/useCancelPlanTermination';
import { useDeletePlanTerminationConfigQuery } from './queries/useDeletePlanTerminationConfig.query';
import { Mode, PlanTerminationCoverageDatesForm } from './PlanTerminationCoverageDatesForm';
import { PlanTerminationDateForm } from './PlanTerminationDateForm';
import { PlanTerminationHsaTransferForm } from './PlanTerminationHsaTransferForm';
    
interface Props {
  onCancel?: (value: boolean) => void;
}
    
const CancelPlanTerminationModalForm: React.FC<Props> = ({
  onCancel,
}) => {  
  const [isServerError, setIsServerError] = useState(false);

  const handleChangeSuccessVisibility = useCancelPlanTerminationSuccessModalStore((state) => state.handleChangeVisibility);

  const {
    currentTerminationDate,
    coverageData,
    isHsaAccount,
    formattedData,
    terminationDataLoading,
    planId,
    planName,
    planData,
  } = useCancelPlanTermination();

  const { handleOpenEditPlanTerminationModal } = useTerminatePlan();
  
  const modalDescription = useMemo(() => {
    return `Please note that you can edit or cancel termination configurations until ${currentTerminationDate}.`;
  }, [currentTerminationDate]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(false);
    }
  }, [onCancel]);

  const { cancelTermination, isLoading } = useDeletePlanTerminationConfigQuery();
    
  const handleSubmit = useCallback(async () => {
    try {
      await cancelTermination(planId || '');
      handleCancel();
      handleChangeSuccessVisibility(true);
    } catch {
      setIsServerError(true);
    }
  }, [
    cancelTermination,
    planId,
    handleCancel,
    handleChangeSuccessVisibility,
  ]);

  const handleEditTermination = useCallback(() => {
    handleCancel();
    handleOpenEditPlanTerminationModal(
      planId as string,
      currentTerminationDate,
      planName,
      isHsaAccount
    );
  }, [
    handleCancel,
    handleOpenEditPlanTerminationModal,
    planId,
    currentTerminationDate,
    planName,
    isHsaAccount,
  ]);
    
  return (
    <>
      <Box margin={{ bottom: 'spacing24' }} align="center">
        <RoundPendingIcon color="warning"/>
        <Box margin={{ vertical: 'spacing12' }}>
          <Inscription size="24px" weight={700} lineHeight="32px" textAlign="center">
            Cancel {planName || ''} Termination?
          </Inscription>
        </Box>
        <Inscription textAlign="center" size="14px">
          {modalDescription}
        </Inscription>
      </Box>
  
      <Box data-testid="CancelPlanTerminationModalForm_container">
        <Box direction="column">

          <Box
            round="container1Round"
            pad="spacing24"
            background="module"
            gap='spacing24'
          >
            {terminationDataLoading ? (
              <Box width="100%">
                <ShimmerBox
                  width="100%"
                  height="88px"
                  background="module"
                  round="container1Round"
                />
              </Box>
            )
            : (
              <PlanTerminationDateForm
                terminationDate={formattedData?.terminationDate}
                viewMode
              />)}
            {terminationDataLoading ? (
              <Box width="100%">
                <ShimmerBox
                  width="100%"
                  height="180px"
                  background="module"
                  round="container1Round"
                />
              </Box>
            ) : (
              <Box gap="spacing24">
                <PlanTerminationCoverageDatesForm mode={Mode.PLANS} planList={planData} />

                <PlanTerminationCoverageDatesForm mode={Mode.COVERAGES} coveragesList={coverageData} />
    
                {isHsaAccount ? (
                  <PlanTerminationHsaTransferForm
                    defaultHsaTransferOfAssets={formattedData?.hsaTransferOfAssets}
                    viewMode
                  />
                ) : null}
              </Box>
            )}
          </Box>

          <Box
            direction="row"
            fill="horizontal"
            margin={{ top: 'spacing24' }}
            align="end"
            justify="between"
            width="medium"
            gap="xs"
          >
            <Box width="160px">
              <AppButton
                testId="CancelPlanTerminationModalForm_edit"
                buttonType="secondary"
                width="100%"
                type="button"
                onClick={handleEditTermination}
                disabled={isLoading}
              >
                {isLoading
                  ? (<Preloader />)
                  : 'Edit Termination'}
              </AppButton>
            </Box>

            <Box direction="row" gap="spacing12">
              <Box width="232px">
                <AppButton
                  testId="CancelPlanTerminationModalForm_cancel"
                  buttonType="secondary"
                  width="100%"
                  type="button"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  {isLoading
                    ? (<Preloader />)
                    : 'Continue With Termination'}
                </AppButton>
              </Box>
              <Box width="232px">
                <AppButton
                  testId="CancelPlanTerminationModalForm_submit"
                  type="button"
                  disabled={isLoading}
                  onClick={handleSubmit}
                  width="100%"
                >
                  {isLoading
                    ? (<Preloader />)
                    : 'Cancel Termination'}
                </AppButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <WarnModal
        testId="plan-termination-server-error-modal"
        visible={isServerError}
        onSetVisible={setIsServerError}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot cancel the Plan termination with the latest records.
          Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try again"
      />
    </>
  );
};
    
export default CancelPlanTerminationModalForm;
    